import { useCallback, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

export const useExperienceFilters = (initialTab = "published") => {
  const [searchParams, setSearchParams] = useSearchParams()
  const categoriesParam = searchParams.get("categories") || ""
  const durationTypesParam = searchParams.get("duration_types") || ""
  // `listings` is how the blackout surfaces deep-link here ("View" on an affected-listings row).
  // The named listings can sit in any status, so such a link opens on the "All" tab rather than
  // the default Published one, where a draft listing would silently drop out of the result.
  const listingsParam = searchParams.get("listings") || ""

  const [activeTab, setActiveTab] = useState("all")
  const [activeListingTab, setActiveListingTab] = useState(listingsParam ? "all" : initialTab)
  const [selectedType, setSelectedType] = useState("private")
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false)

  const selectedCategoryIds = useMemo(
    () => (categoriesParam ? categoriesParam.split(",").map(Number).filter(Boolean) : []),
    [categoriesParam]
  )
  const selectedDurationTypes = useMemo(
    () => (durationTypesParam ? durationTypesParam.split(",").filter(Boolean) : []),
    [durationTypesParam]
  )
  // Listing ids stay strings: they arrive from the blackout endpoints, which serialize ids as
  // strings, and are compared against the chip values built from the same param.
  const selectedListingIds = useMemo(
    () => (listingsParam ? listingsParam.split(",").filter(Boolean) : []),
    [listingsParam]
  )
  const activeFilterCount = selectedCategoryIds.length + selectedDurationTypes.length + selectedListingIds.length

  const buildFilterQuery = useCallback(() => {
    const q = {}
    if (activeTab !== "all") q.experience_type_eq = activeTab
    if (selectedCategoryIds.length > 0) q.category_ids_in = selectedCategoryIds
    if (selectedDurationTypes.length > 0) q.duration_type_in = selectedDurationTypes
    if (selectedListingIds.length > 0) q.id_in = selectedListingIds
    return q
  }, [activeTab, selectedCategoryIds, selectedDurationTypes, selectedListingIds])

  const applyFilters = useCallback(
    // `listingIds` defaults to what is already applied so the category/duration dropdown, which
    // knows nothing about it, does not drop the deep-linked listing filter on every apply.
    ({ categoryIds = [], durationTypes = [], listingIds = selectedListingIds }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (categoryIds.length > 0) next.set("categories", categoryIds.join(","))
          else next.delete("categories")
          if (durationTypes.length > 0) next.set("duration_types", durationTypes.join(","))
          else next.delete("duration_types")
          if (listingIds.length > 0) next.set("listings", listingIds.join(","))
          else next.delete("listings")
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams, selectedListingIds]
  )

  const clearFilters = useCallback(() => applyFilters({ categoryIds: [], durationTypes: [], listingIds: [] }), [applyFilters])

  const removeCategoryFilter = useCallback(
    (id) => applyFilters({ categoryIds: selectedCategoryIds.filter((v) => v !== id), durationTypes: selectedDurationTypes }),
    [applyFilters, selectedCategoryIds, selectedDurationTypes]
  )

  const removeDurationTypeFilter = useCallback(
    (value) => applyFilters({ categoryIds: selectedCategoryIds, durationTypes: selectedDurationTypes.filter((v) => v !== value) }),
    [applyFilters, selectedCategoryIds, selectedDurationTypes]
  )

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
  }, [])

  const handleListingTabChange = useCallback((tab) => {
    setActiveListingTab(tab)
    setSelectedType("")
  }, [])

  const handleFilterIconClick = useCallback(() => {
    setIsFilterDropdownOpen((prev) => !prev)
  }, [])

  const handleCloseFilterDropdown = useCallback(() => {
    setIsFilterDropdownOpen(false)
  }, [])

  return {
    activeTab,
    activeListingTab,
    selectedType,
    isFilterDropdownOpen,
    handleTabChange,
    handleListingTabChange,
    setSelectedType,
    handleFilterIconClick,
    handleCloseFilterDropdown,
    selectedCategoryIds,
    selectedDurationTypes,
    selectedListingIds,
    activeFilterCount,
    buildFilterQuery,
    applyFilters,
    clearFilters,
    removeCategoryFilter,
    removeDurationTypeFilter
  }
}
